import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
} from "@mui/material";

function PayItems() {
  const [payItems, setPayItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({ id: null, name: "" });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchPayItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/pay-items");
      const payload = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      setPayItems(payload);
    } catch (error) {
      console.error("Failed to fetch pay items", error);
      setSnackbar({
        open: true,
        message: "Failed to load pay items.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayItems();
  }, []);

  const handleOpenCreate = () => {
    setCurrentItem({ id: null, name: "" });
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setCurrentItem({
      id: item?.id ?? null,
      name: item?.name ?? "",
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!currentItem.name.trim()) {
      setSnackbar({
        open: true,
        message: "Pay item name is required.",
        severity: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      if (currentItem.id) {
        await axios.put(`/pay-items/${currentItem.id}`, {
          name: currentItem.name.trim(),
        });
        setSnackbar({
          open: true,
          message: "Pay item updated successfully.",
          severity: "success",
        });
      } else {
        await axios.post("/pay-items", {
          name: currentItem.name.trim(),
        });
        setSnackbar({
          open: true,
          message: "Pay item created successfully.",
          severity: "success",
        });
      }

      setOpenDialog(false);
      setCurrentItem({ id: null, name: "" });
      fetchPayItems();
    } catch (error) {
      console.error("Save failed", error);
      setSnackbar({
        open: true,
        message: "Unable to save pay item.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this pay item?")) return;

    try {
      await axios.delete(`/pay-items/${id}`);
      setSnackbar({
        open: true,
        message: "Pay item deleted successfully.",
        severity: "success",
      });
      fetchPayItems();
    } catch (error) {
      console.error("Delete failed", error);
      setSnackbar({
        open: true,
        message: "Unable to delete pay item.",
        severity: "error",
      });
    }
  };

  const paginatedItems = useMemo(
    () => payItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [payItems, page, rowsPerPage]
  );

  return (
    <Box>
      <Card sx={{ borderRadius: 3, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
        <CardContent>
          <Grid container justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
            <Grid item>
              <Typography variant="h4" fontWeight={700}>
                Pay Items
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create, update, and manage payroll pay items.
              </Typography>
            </Grid>

            <Grid item>
              <Button variant="contained" onClick={handleOpenCreate}>
                Add New
              </Button>
            </Grid>
          </Grid>

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
            <Table>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : paginatedItems.length > 0 ? (
                  paginatedItems.map((item, index) => (
                    <TableRow key={item?.id || index} hover>
                      <TableCell width={80}>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{item?.name || "-"}</TableCell>
                      <TableCell align="right" width={140}>
                        <IconButton color="primary" onClick={() => handleEdit(item)}>
                          <Icon>edit</Icon>
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(item.id)}>
                          <Icon>delete</Icon>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No pay items found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={payItems.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{currentItem.id ? "Edit Pay Item" : "Add Pay Item"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            label="Pay Item Name"
            value={currentItem.name}
            onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PayItems;
